import { useEffect, useState } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, limit } from 'firebase/firestore';

const formatMoney = (value) => `$${Number(value || 0).toLocaleString('es-CO', { minimumFractionDigits: 0 })}`;
const getCalculatedSalary = (receipt) =>
  receipt?.payrollType === 'dailyAttendance'
    ? Number(receipt?.totals?.calculatedTotal || 0)
    : Number(receipt?.devengados?.salario || 0);
const getNetTotal = (receipt) =>
  Number(
    receipt?.payrollType === 'dailyAttendance'
      ? receipt?.totals?.finalWithAdjustment ?? receipt?.totals?.final ?? receipt?.netSalary ?? receipt?.neto ?? 0
      : receipt?.netSalary ?? receipt?.neto ?? 0
  );
const getDeductionsTotal = (receipt) =>
  receipt?.payrollType === 'dailyAttendance' ? 0 : Number(receipt?.deductions?.total || 0);
const getGrossTotal = (receipt) =>
  Number(
    receipt?.payrollType === 'dailyAttendance'
      ? receipt?.totals?.salarySubtotal ?? receipt?.totals?.calculatedTotal ?? 0
      : receipt?.gross || 0
  );

const REPORT_LABELS = {
  summary: 'Resumen General',
  employeeDetails: 'Detalle por Empleado',
  monthlySummary: 'Resumen Mensual',
  costByMonth: 'Costos por Mes',
};

const escapeCsv = (value) => {
  const str = value === null || value === undefined ? '' : String(value);
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const buildCsvReport = ({ reportType, reportData, includeDrafts }) => {
  const rows = [];
  rows.push(['Reporte', REPORT_LABELS[reportType] || reportType]);
  rows.push(['Generado', new Date().toLocaleString('es-CO')]);
  rows.push(['Incluye borradores', includeDrafts ? 'Si' : 'No']);
  rows.push(['', '']);

  if (reportType === 'summary') {
    rows.push(['Concepto', 'Valor']);
    rows.push(['Total Salarios Brutos', Number(reportData?.totalSalaries || 0)]);
    rows.push(['Total Deducciones', Number(reportData?.totalDeductions || 0)]);
    rows.push(['Total Nomina Neta', Number(reportData?.totalNetPayroll || 0)]);
    rows.push(['Numero de Empleados', Number(reportData?.employeeCount || 0)]);
  } else if (reportType === 'employeeDetails') {
    rows.push(['Empleado', 'Salario Base', 'Deducciones', 'Salario Neto']);
    (Array.isArray(reportData) ? reportData : []).forEach((emp) => {
      rows.push([
        emp?.name || '',
        Number(emp?.baseSalary || 0),
        Number(emp?.deductions || 0),
        Number(emp?.netSalary || 0),
      ]);
    });
  } else if (reportType === 'monthlySummary') {
    rows.push(['Mes', 'Salario Bruto', 'Deducciones', 'Salario Neto']);
    (Array.isArray(reportData) ? reportData : []).forEach((item) => {
      rows.push([
        item?.month || '',
        Number(item?.gross || 0),
        Number(item?.deductions || 0),
        Number(item?.net || 0),
      ]);
    });
  } else if (reportType === 'costByMonth') {
    rows.push(['Mes', 'Costo Total de Nomina']);
    (Array.isArray(reportData) ? reportData : []).forEach((item) => {
      rows.push([item?.month || '', Number(item?.totalCost || 0)]);
    });
  }

  const sep = ';';
  return ['sep=;', ...rows.map((row) => row.map(escapeCsv).join(sep))].join('\n');
};

const buildPdfHtml = ({ reportType, reportData, includeDrafts }) => {
  const label = REPORT_LABELS[reportType] || reportType;
  const generated = new Date().toLocaleString('es-CO');
  let tableHead = '';
  let tableBody = '';

  if (reportType === 'summary') {
    tableHead = '<tr><th>Concepto</th><th>Valor</th></tr>';
    tableBody = `
      <tr><td>Total Salarios Brutos</td><td class="num">${formatMoney(reportData?.totalSalaries)}</td></tr>
      <tr><td>Total Deducciones</td><td class="num">-${formatMoney(reportData?.totalDeductions)}</td></tr>
      <tr><td>Total Nomina Neta</td><td class="num">${formatMoney(reportData?.totalNetPayroll)}</td></tr>
      <tr><td>Numero de Empleados</td><td class="num">${reportData?.employeeCount || 0}</td></tr>
    `;
  } else if (reportType === 'employeeDetails') {
    tableHead = '<tr><th>Empleado</th><th>Salario Base</th><th>Deducciones</th><th>Salario Neto</th></tr>';
    tableBody = (Array.isArray(reportData) ? reportData : [])
      .map((emp) => `
        <tr>
          <td>${emp?.name || ''}</td>
          <td class="num">${formatMoney(emp?.baseSalary)}</td>
          <td class="num">${formatMoney(emp?.deductions)}</td>
          <td class="num">${formatMoney(emp?.netSalary)}</td>
        </tr>
      `).join('');
  } else if (reportType === 'monthlySummary') {
    tableHead = '<tr><th>Mes</th><th>Salario Bruto</th><th>Deducciones</th><th>Salario Neto</th></tr>';
    tableBody = (Array.isArray(reportData) ? reportData : [])
      .map((item) => `
        <tr>
          <td>${item?.month || ''}</td>
          <td class="num">${formatMoney(item?.gross)}</td>
          <td class="num">${formatMoney(item?.deductions)}</td>
          <td class="num">${formatMoney(item?.net)}</td>
        </tr>
      `).join('');
  } else if (reportType === 'costByMonth') {
    tableHead = '<tr><th>Mes</th><th>Costo Total de Nomina</th></tr>';
    tableBody = (Array.isArray(reportData) ? reportData : [])
      .map((item) => `
        <tr>
          <td>${item?.month || ''}</td>
          <td class="num">${formatMoney(item?.totalCost)}</td>
        </tr>
      `).join('');
  }

  if (!tableBody) {
    tableBody = '<tr><td colspan="4" class="empty">Sin datos para exportar.</td></tr>';
  }

  return `
  <html>
    <head>
      <meta charset="utf-8" />
      <title>Reporte de Nomina</title>
      <style>
        @page { size: A4 portrait; margin: 14mm; }
        body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; color:#111; }
        h1 { font-size: 18px; margin: 0 0 6px; }
        .meta { font-size: 12px; color:#555; margin-bottom: 12px; }
        table { width:100%; border-collapse: collapse; font-size: 12px; }
        th, td { border:1px solid #ddd; padding:6px 8px; }
        th { background:#f5f5f5; text-align:left; }
        td.num { text-align:right; white-space: nowrap; }
        tr { page-break-inside: avoid; }
        .empty { text-align:center; color:#666; }
      </style>
    </head>
    <body>
      <h1>Reporte de Nomina - ${label}</h1>
      <div class="meta">Generado: ${generated} | Incluye borradores: ${includeDrafts ? 'Si' : 'No'}</div>
      <table>
        <thead>${tableHead}</thead>
        <tbody>${tableBody}</tbody>
      </table>
      <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 300); };</script>
    </body>
  </html>`;
};

const PayrollReportsView = ({ onBack, currentUser }) => {
  const [reportType, setReportType] = useState('summary');
  const [reportData, setReportData] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [includeDrafts, setIncludeDrafts] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [recSnap, empSnap] = await Promise.all([
          getDocs(query(collection(db, 'payrollReceipts'), limit(300))),
          getDocs(query(collection(db, 'employees'), limit(300))),
        ]);
        if (cancelled) return;
        setReceipts(recSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setEmployees(empSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('No se pudo cargar nómina', err);
        setReceipts([]);
        setEmployees([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const handleGenerateReport = () => {
    const usable = includeDrafts ? receipts : receipts.filter((r) => (r.status || 'borrador') === 'aprobado');
    let data = null;
    if (reportType === 'summary') {
      const totalSalaries = usable.reduce((s, r) => s + getCalculatedSalary(r), 0);
      const totalDeductions = usable.reduce((s, r) => s + getDeductionsTotal(r), 0);
      const totalNetPayroll = usable.reduce((s, r) => s + getNetTotal(r), 0);
      data = {
        totalSalaries,
        totalDeductions,
        totalNetPayroll,
        employeeCount: employees.length,
      };
    } else if (reportType === 'employeeDetails') {
      const byEmp = {};
      for (const r of usable) {
        const k = r.employeeId;
        if (!byEmp[k]) byEmp[k] = [];
        byEmp[k].push(r);
      }
      data = employees.map((emp) => {
        const arr = byEmp[emp.id] || [];
        const accNet = arr.reduce((s, r) => s + getNetTotal(r), 0);
        const accDed = arr.reduce((s, r) => s + getDeductionsTotal(r), 0);
        return { id: emp.id, name: emp.name, baseSalary: Number(emp.baseSalary || 0), netSalary: accNet, deductions: accDed };
      });
    } else if (reportType === 'monthlySummary') {
      const group = new Map();
      for (const r of usable) {
        const k = String(r.payDate || '').slice(0, 7);
        if (!group.has(k)) group.set(k, { gross: 0, net: 0, deductions: 0 });
        const o = group.get(k);
        o.gross += getGrossTotal(r);
        o.net += getNetTotal(r);
        o.deductions += getDeductionsTotal(r);
      }
      data = Array.from(group.entries()).map(([month, v]) => ({ month, ...v }));
    } else if (reportType === 'costByMonth') {
      const group = new Map();
      for (const r of usable) {
        const k = String(r.payDate || '').slice(0, 7);
        if (!group.has(k)) group.set(k, 0);
        group.set(k, group.get(k) + getGrossTotal(r));
      }
      data = Array.from(group.entries()).map(([month, totalCost]) => ({ month, totalCost }));
    }
    setReportData(data);
  };

  const handleExportExcel = () => {
    if (!reportData) {
      alert('Primero genera un reporte para exportar.');
      return;
    }
    const csv = buildCsvReport({ reportType, reportData, includeDrafts });
    const stamp = new Date().toISOString().slice(0, 10);
    const filename = `reporte-nomina-${reportType}-${stamp}.csv`;
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, filename);
  };

  const handleExportPDF = () => {
    if (!reportData) {
      alert('Primero genera un reporte para exportar.');
      return;
    }
    const html = buildPdfHtml({ reportType, reportData, includeDrafts });
    const w = window.open('', '_blank');
    if (!w) {
      alert('El navegador bloqueó la ventana de impresión.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="p-4 sm:p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center mb-6 sm:mb-8">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 transition duration-200 px-4 py-2 rounded-lg border border-blue-200 hover:border-blue-400 w-full md:w-auto justify-center md:justify-start"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al Menu
        </button>
        <h1 className="text-2xl sm:text-4xl font-extrabold text-gray-900 drop-shadow-sm text-center md:text-left">Informes y Reportes de Nomina</h1>
        <div className="hidden md:block w-32"></div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 w-full max-w-3xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 text-center">Generar Reporte</h2>
        <div className="grid grid-cols-1 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Reporte</label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
            >
              <option value="summary">Resumen General</option>
              <option value="employeeDetails">Detalle por Empleado</option>
              <option value="monthlySummary">Resumen Mensual</option>
              <option value="costByMonth">Costos por Mes</option>
            </select>
          </div>
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="includeDrafts"
              checked={includeDrafts}
              onChange={(e) => setIncludeDrafts(e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <label htmlFor="includeDrafts" className="text-sm text-gray-700">Incluir borradores (recibos no aprobados)</label>
          </div>
        </div>
        <div className="flex justify-center">
          <button
            onClick={handleGenerateReport}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg text-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200"
          >
            Generar Reporte
          </button>
        </div>

        {reportData && (
          <div className="mt-10 p-6 border border-gray-200 rounded-lg shadow-md bg-gray-50">
            <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">Resultados del Reporte</h3>
            {reportType === 'summary' && (
              <div className="space-y-2">
                <p className="flex justify-between text-lg"><span>Total Salarios Brutos:</span> <strong>{formatMoney(reportData?.totalSalaries)}</strong></p>
                <p className="flex justify-between text-lg"><span>Total Deducciones:</span> <strong>-{formatMoney(reportData?.totalDeductions)}</strong></p>
                <p className="flex justify-between text-lg"><span>Total Nomina Neta:</span> <strong>{formatMoney(reportData?.totalNetPayroll)}</strong></p>
                <p className="flex justify-between text-lg"><span>Numero de Empleados:</span> <strong>{reportData.employeeCount}</strong></p>
              </div>
            )}
            {reportType === 'employeeDetails' && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-white">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empleado</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salario Base</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deducciones</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salario Neto</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(Array.isArray(reportData) ? reportData : []).map((emp) => (
                      <tr key={emp.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{emp.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatMoney(emp?.baseSalary)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatMoney(emp?.deductions)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatMoney(emp?.netSalary)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {reportType === 'monthlySummary' && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-white">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mes</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salario Bruto</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deducciones</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salario Neto</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(Array.isArray(reportData) ? reportData : []).map((item, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.month}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatMoney(item?.gross)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatMoney(item?.deductions)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatMoney(item?.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {reportType === 'costByMonth' && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-white">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mes</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Costo Total de Nomina</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(Array.isArray(reportData) ? reportData : []).map((item, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.month}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatMoney(item?.totalCost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={handleExportExcel}
                className="px-4 sm:px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200 shadow-sm w-full sm:w-auto"
              >
                Exportar a Excel
              </button>
              <button
                onClick={handleExportPDF}
                className="px-4 sm:px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 shadow-md w-full sm:w-auto"
              >
                Exportar a PDF
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollReportsView;


